import { HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpInterceptor } from '@angular/common/http';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { Routes, RouterModule, Router } from '@angular/router';
import { mergeMap, catchError, map, timeout, filter, take } from 'rxjs/operators';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthService } from './auth.service';
import moment from 'moment';
import * as CryptoJS from 'crypto-js';
import { environment } from 'src/environments/environment';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { shareReplay } from 'rxjs/operators';


@Injectable()
// export class AuthTokenInterceptor extends MsalService {
export class AuthTokenInterceptor implements HttpInterceptor {
    public isTokenExpired = false;
    private renewal$: Observable<any> | null = null;
    private static redirectTriggered = false;
    private interactionStatus: InteractionStatus = InteractionStatus.Startup;
    private tokenRefreshTimer: any;
    private readonly TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
    private readonly TIMEOUT_MS = 30000; // Increased from 15s to 30s
    private readonly RETRY_ATTEMPTS = 3;
    private readonly RETRY_DELAY_MS = 2000;

    constructor(
        private msalService: MsalService,
        private brodcast: MsalBroadcastService,
        private authService: AuthService,
        private router: Router
    ) {
        this.brodcast.inProgress$.subscribe(s => {
            this.interactionStatus = s;
            // Reset redirect flag when MSAL interaction completes
            if (s === InteractionStatus.None) {
                AuthTokenInterceptor.redirectTriggered = false;
            }
        });

        // Initialize proactive token refresh mechanism
        this.initializeTokenRefreshScheduler();
    }

    private initializeTokenRefreshScheduler() {
        // Periodically check and refresh token before it expires
        setInterval(() => {
            try {
                const tokenExpTime = this.authService.getTokenExpiryTime();
                if (tokenExpTime) {
                    const timeUntilExpiry = moment(tokenExpTime).diff(moment(), 'ms');
                    // If token will expire within buffer window, proactively refresh
                    if (timeUntilExpiry > 0 && timeUntilExpiry < this.TOKEN_REFRESH_BUFFER_MS) {
                        console.log('%c[TokenRefreshScheduler] Proactively refreshing token before expiry', {
                            minutesUntilExpiry: (timeUntilExpiry / 60000).toFixed(2),
                            bufferMinutes: (this.TOKEN_REFRESH_BUFFER_MS / 60000)
                        });
                        this.getADToken().subscribe(
                            (res) => {
                                if (res?.type === 'Success') {
                                    console.log('%c[TokenRefreshScheduler] Proactive refresh SUCCESS');
                                }
                            },
                            (err) => {
                                console.error('%c[TokenRefreshScheduler] Proactive refresh failed', err);
                            }
                        );
                    }
                }
            } catch (error) {
                console.warn('%c[TokenRefreshScheduler] Error during scheduled check', error);
            }
        }, 60000); // Check every minute
    }

    private clearCacheIfStale() {
        try {
            const lastTokenTime = localStorage.getItem('token_expiry_time');
            if (lastTokenTime) {
                // If token timestamp is more than 2 days old, clear cache
                const tokenAge = moment().diff(moment(lastTokenTime), 'days');
                if (tokenAge > 2) {
                    console.log('%c[ClearCache] Token cache is stale (>2 days old), clearing', { tokenAge });
                    this.clearAuthCache();
                }
            }
        } catch (error) {
            console.warn('%c[ClearCache] Error checking cache staleness', error);
        }
    }

    private clearAuthCache() {
        try {
            // Clear MSAL cache
            const accounts = this.msalService.instance.getAllAccounts();
            if (accounts && accounts.length > 0) {
                this.msalService.instance.clearCache();
            }
            // Clear application-specific tokens
            localStorage.removeItem('idToken');
            localStorage.removeItem('graphToken');
            localStorage.removeItem('ad_token');
            localStorage.removeItem('token_expiry_time');
        } catch (error) {
            console.error('%c[ClearCache] Error clearing cache', error);
        }
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // LOG 1: Track interceptor execution

        // Allow static assets to pass through without authentication
        // Check both relative paths (./assets/) and absolute paths (/assets/)
        if (req.url.includes('/assets/') || req.url.startsWith('./assets/')) {
            return next.handle(req);
        }

        // WAIT FOR MSAL TO FINISH INITIALIZING before processing requests
        if (this.interactionStatus === InteractionStatus.Startup) {


            // Wait for MSAL initialization to complete, then retry
            return this.brodcast.inProgress$.pipe(
                filter((status) => status === InteractionStatus.None),
                take(1),
                mergeMap(() => {
                    // Recursively call intercept after MSAL is ready
                    return this.intercept(req, next);
                })
            );
        }
        const account = this.getAccountSafely();
        if (account) {
            this.msalService.instance.setActiveAccount(account);
            const authReq = this.addADTokenToHeaders(req);
            const tokenExpTime = this.authService.getTokenExpiryTime();
            const currentDateTime = moment().format('YYYY-MM-DD HH:mm:ss');
            const isSameOrBefore = moment(tokenExpTime).isSameOrBefore(currentDateTime);

            // LOG 2: Token expiry check details

            // let user = '';
            // var key = CryptoJS.enc.Utf8.parse(environment.subscriptionKey);
            // var iv = CryptoJS.enc.Utf8.parse('8080808080808080');
            // if (account.username) {
            //     user = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(account.username), key,
            //         {
            //             keySize: 128 / 8,
            //             iv: iv,
            //             mode: CryptoJS.mode.CBC,
            //             padding: CryptoJS.pad.Pkcs7
            //         }).toString();
            // }

            // CHECK: If idToken is missing from localStorage but account exists, 
            // it means token was cleared or expired. Try to renew it immediately.
            const storedIdToken = localStorage.getItem('idToken');
            if (!storedIdToken && account && this.interactionStatus === InteractionStatus.None) {

                return this.getADToken().pipe(
                    mergeMap((res: any) => {


                        if (res && res.type === 'Success') {
                            const finalReq = this.addADTokenToHeaders(req);
                            return next.handle(finalReq);
                        } else {
                            return throwError(() => new Error('Token renewal failed for missing idToken'));
                        }
                    }),
                    catchError((error) => {
                        return throwError(() => error);
                    })
                );
            }

            if (tokenExpTime && currentDateTime && isSameOrBefore) {

                if (this.interactionStatus !== InteractionStatus.None) {
                    return next.handle(this.addADTokenToHeaders(req));
                }

                return this.getADToken().pipe(
                    mergeMap((res: any) => {
                        // LOG 3: Token renewal result


                        if (res && res.type && res.type === 'Success') {
                            const finalReq = this.addADTokenToHeaders(req);
                            return next.handle(finalReq);
                        } else {
                            console.error('%c[TokenRenewal] FAILED - Renewal returned non-success', res);
                            this.isTokenExpired = true;
                            // Return error to upstream instead of EMPTY to prevent hanging requests
                            const errorMsg = res?.info?.errorMessage || res?.info?.error || 'Token renewal failed';
                            return throwError(() => new Error(errorMsg));
                        }
                    }),
                    catchError((error) => {
                        console.error('%c[TokenRenewal] FAILED - Error during renewal', {
                            errorCode: error?.errorCode,
                            errorMessage: error?.errorMessage || error?.message,
                            error: error,
                            timestamp: new Date().toISOString()
                        });
                        this.isTokenExpired = true;
                        // Return error to upstream instead of EMPTY
                        return throwError(() => error);
                    })
                );
            }

            // LOG 4: Normal token check

            const jwt = localStorage.getItem('idToken');
            const token: string | null = req.headers.get('Authorization');
            if (token && !jwt) {
                localStorage.setItem('graphToken', token);
            }
            if (!!jwt && token == null) {
                const tken = jwt.includes('Bearer ') ? `${jwt}` : `Bearer ${jwt}`;
                req = req.clone({
                    headers: req.headers.set('Authorization', tken)
                });
            }
            if (token) {
                const tken = token.includes('Bearer ') ? `${token}` : `Bearer ${token}`;
                req = req.clone({
                    headers: req.headers.set('Authorization', tken)
                });
                localStorage.setItem('graphToken', token);
            }
            req = req.clone({
                headers: req.headers
                    .set('X-Frame-Options', 'deny')
                    .set('X-XSS-Protection', `1`)
                    .set('Strict-Transport-Security', `max-age=31536000`)
                    .set('X-Content-Type-Options', `nosniff`)
                    .set('Permissions-Policy', `autoplay=*; geolocation=*; fullscreen=()`)
                    .set('Feature-Policy', `vibrate 'self'; usermedia *; sync-xhr 'self'`)
            });
            return next.handle(req).pipe(
                catchError((error: HttpErrorResponse) => {
                    return throwError(() => error);
                })
            );
        } else {
            // No account found - wait for MSAL status or pass through request


            // If MSAL still initializing, wait for it to complete
            if (this.interactionStatus !== InteractionStatus.None) {
                return this.brodcast.inProgress$.pipe(
                    filter((status) => status === InteractionStatus.None),
                    take(1),
                    mergeMap(() => {
                        return this.intercept(req, next);
                    })
                );
            }

            // MSAL is ready but no account - user is not authenticated
            // Guard already blocked protected routes, so this is likely for public routes or initialization
            // Don't redirect here - let app.component and guards handle authentication flow

            // Add security headers but no auth token
            const secureReq = req.clone({
                headers: req.headers
                    .set('X-Frame-Options', 'deny')
                    .set('X-XSS-Protection', '1')
                    .set('X-Content-Type-Options', 'nosniff')
                    .set('Permissions-Policy', 'autoplay=*; geolocation=*; fullscreen=()')
                    .set('Feature-Policy', 'vibrate \'self\'; usermedia *; sync-xhr \'self\'')
            });

            return next.handle(secureReq).pipe(
                catchError((error: HttpErrorResponse) => {
                    return throwError(() => error);
                })
            );
        }
    }


    // modify headers with AD token
    public addADTokenToHeaders(req: HttpRequest<any>): HttpRequest<any> {
        const account = this.getAccountSafely();
        // let user = '';
        // var key = CryptoJS.enc.Utf8.parse(environment.subscriptionKey);
        // var iv = CryptoJS.enc.Utf8.parse('8080808080808080');
        // if (account && account.username) {
        //     user = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(account.username), key,
        //         {
        //             keySize: 128 / 8,
        //             iv: iv,
        //             mode: CryptoJS.mode.CBC,
        //             padding: CryptoJS.pad.Pkcs7
        //         }).toString();
        // }

        const authToken = localStorage.getItem('idToken');

        // LOG 5: Header injection details

        if (!authToken) {
            console.warn('%c[AddHeaders] No auth token in localStorage, returning request without Authorization header');
            return req;
        }

        // Clone the request and replace the original headers with cloned headers, updated with the authorization.
        const authReq = req.clone({
            headers: req.headers
                .set('Authorization', `Bearer ${authToken}`)
                .set('X-Frame-Options', 'deny')
                .set('X-XSS-Protection', `1`)
                .set('X-Content-Type-Options', `nosniff`)
                .set('Permissions-Policy', `autoplay=*; geolocation=*; fullscreen=()`)
                .set('Feature-Policy', `vibrate 'self'; usermedia *; sync-xhr 'self'`)
        });


        return authReq;
    }

    // Get AD token silently with retry logic and improved timeout handling
    public getADToken(retryAttempt: number = 0): Observable<any> {
        // LOG 6: Renewal observable caching
        if (this.renewal$ && retryAttempt === 0) {
            return this.renewal$;
        }

        const account = this.getAccountSafely();
        if (!account) {
            console.error('%c[GetADToken] No account available');
            return of({ type: 'Error', info: 'No active account available for silent token acquisition.' });
        }


        // Skip silent acquisition if browser tab is hidden - it will fail anyway
        if (document.hidden) {
            console.warn('%c[GetADToken] Browser tab is hidden, skipping silent acquisition (it will timeout)');
            // Make the tab visible or wait - but for now, return error to trigger interactive login
            return of({ type: 'Error', info: 'Browser tab is hidden. User interaction required.' });
        }

        // To renew idToken, please pass clientId as the only scope
        const accessTokenRequest = {
            scopes: ['user.read'],
            authority: environment.config.authority,
            account
        };

        this.renewal$ = this.msalService.acquireTokenSilent(accessTokenRequest).pipe(
            timeout(this.TIMEOUT_MS),  // Increased timeout from 15s to 30s
            map((payload: any) => {
                // LOG 7: Token acquisition success

                if (retryAttempt === 0) {
                    this.renewal$ = null;
                }

                if (payload && payload.expiresOn) {
                    this.authService.setTokenExpiryTime(payload);
                }

                if (payload && payload.idToken) {
                    this.authService.setADToken(payload.idToken);
                    localStorage.setItem('idToken', payload.idToken);
                }
                if (payload && payload.idToken) {
                    // Store access token for graph calls
                    localStorage.setItem('graphToken', payload.idToken);
                }
                if (payload && payload.accessToken) {
                    // Store access token for graph calls
                    localStorage.setItem('graphToken', payload.accessToken);
                }

                return { type: 'Success', info: payload };
            }),
            catchError((error) => {
                if (retryAttempt === 0) {
                    this.renewal$ = null;
                }
                // Check if this is a timeout error
                const isTimeoutError = error?.name === 'TimeoutError' ||
                    error?.message?.includes('timeout') ||
                    error?.errorCode === 'timed_out' ||
                    error?.subError === 'redirect_bridge_timeout';

                if (isTimeoutError && retryAttempt < this.RETRY_ATTEMPTS - 1) {
                    console.log('%c[GetADToken] Timeout error detected, retrying', {
                        retryAttempt: retryAttempt,
                        maxAttempts: this.RETRY_ATTEMPTS,
                        nextRetryDelayMs: this.RETRY_DELAY_MS * (retryAttempt + 1)
                    });

                    // Exponential backoff retry
                    const delayMs = this.RETRY_DELAY_MS * (retryAttempt + 1);
                    return of(null).pipe(
                        // Use timer instead of delay to avoid RxJS operator
                        mergeMap(() => new Observable(subscriber => {
                            const timeoutId = setTimeout(() => {
                                subscriber.next(null);
                                subscriber.complete();
                            }, delayMs);
                            return () => clearTimeout(timeoutId);
                        })),
                        mergeMap(() => this.getADToken(retryAttempt + 1))
                    );
                }

                // LOG 8: Token acquisition failure


                console.error('Silent token acquisition failed:', error);

                // If silent acquisition fails, trigger interactive login
                if ((error instanceof InteractionRequiredAuthError || isTimeoutError) &&
                    this.interactionStatus === InteractionStatus.None &&
                    !AuthTokenInterceptor.redirectTriggered) {
                    AuthTokenInterceptor.redirectTriggered = true;
                    localStorage.setItem('redirect_back_url', this.router.url);

                    // Redirect to login - this should interrupt current flow
                    this.msalService.instance.loginRedirect();
                    // Return EMPTY to allow redirect to happen without error
                    return EMPTY;
                }

                // For other errors, return error response
                return of({ type: 'Error', info: error });
            }),
            shareReplay(1)
        );
        return this.renewal$;
    }

    private getAccountSafely() {
        try {
            const activeAccount = this.msalService.instance.getActiveAccount();
            if (activeAccount) {
                return activeAccount;
            }
            const accounts = this.msalService.instance.getAllAccounts();
            return accounts.length > 0 ? accounts[0] : null;
        } catch (error) {
            console.warn('MSAL not initialized yet while resolving account.', error);
            return null;
        }
    }
}